import z from "zod";

import { SIZES } from "@frontend/styles/constants";

import { SELECT_LAYOUTS, SELECT_VARIANTS } from "./constants";

const selectOptionSchema = z
  .object({
    id: z.string(),
    text: z.string(),
    group: z
      .string()
      .optional()
      .describe(
        "Label of the enclosing <rwc-select-group>, if the option is grouped.",
      ),
  })
  .catchall(z.unknown());

export type SelectOption = z.infer<typeof selectOptionSchema>;

const booleanAttribute = z
  .string()
  .optional()
  .transform((value) => value !== undefined && value !== "false");

const optionalBooleanAttribute = z
  .string()
  .optional()
  .transform((value) => (value === undefined ? undefined : value !== "false"));

export const selectDataSchema = z.object({
  items: z
    .array(selectOptionSchema)
    .default([])
    .describe(
      "Options parsed from the light DOM (<rwc-select-item>/<rwc-select-group> elements inside <rwc-select>) — not a Twig-authored attribute.",
    ),
  defaultValue: z
    .string()
    .nullable()
    .optional()
    .describe(
      "Initially selected option id, read from data-initial-id or the selected <rwc-select-item> — not settable after mount, use el.select(id) instead.",
    ),
  placeholder: z.string().optional(),
  variant: z
    .enum(SELECT_VARIANTS)
    .optional()
    .describe(
      'Trigger chrome: "ghost" drops the border and background, for a Select that sits inside another field such as an Input section.',
    ),
  layout: z
    .enum(SELECT_LAYOUTS)
    .optional()
    .describe(
      'Form factor: "text" drops the box so the Select sits inside a sentence, taking the typography around it. The popup then sizes itself instead of following the trigger.',
    ),
  size: z.enum(SIZES).optional(),
  disabled: booleanAttribute,
  required: booleanAttribute,
  name: z.string().optional(),
  alignItemWithTrigger: optionalBooleanAttribute.describe(
    'Lands the selected row on the trigger instead of dropping the popup below it. Keeps the popup on the trigger\'s width, so turn it off to use popupWidth. Defaults to on, except in the "text" layout.',
  ),
  popupWidth: z
    .string()
    .optional()
    .describe(
      'Popup width: "anchor" (default, matches the trigger), "content" (widest option, capped by the space left on screen), or any CSS length such as "32em". Never narrower than the trigger, and ignored unless alignItemWithTrigger is off.',
    ),
  onValueChange: z
    .custom<(id: string | null, item: SelectOption | null) => void>()
    .optional()
    .describe(
      "Internal callback bound by readSelectSlotted — not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
});

export type SelectDataProps = z.infer<typeof selectDataSchema>;
